export type SnakeCaseToCamelCase<S extends string> =
  S extends `${infer A}_${infer B}${infer C}`
    ? `${A}${Capitalize<B>}${SnakeCaseToCamelCase<C>}`
    : S;

export const snakeCaseToCamelCase = <S extends string>(
  str: S
): SnakeCaseToCamelCase<S> => {
  return str.replace(/_([a-zA-Z0-9])/g, (_, letter: string) =>
    letter.toUpperCase()
  ) as SnakeCaseToCamelCase<S>;
};

type CamelCaseToSnakeCase<S extends string> = Lowercase<
  S extends `${infer H}${infer T}`
    ? // Skip if head is not a letter:
      H extends Capitalize<H> & Uncapitalize<H>
      ? `${H}${CamelCaseToSnakeCase<T>}`
      : // Skip if the tail does not start with a letter:
        T extends Capitalize<T> & Uncapitalize<T>
        ? `${H}${CamelCaseToSnakeCase<T>}`
        : // Add an underscore beween a lowercase letter and an uppercase letter:
          [H, T] extends [Lowercase<H>, Capitalize<T>]
          ? `${H}_${CamelCaseToSnakeCase<T>}`
          : `${H}${CamelCaseToSnakeCase<T>}`
    : S
>;

export const camelCaseToSnakeCase = <S extends string>(
  str: S
): CamelCaseToSnakeCase<S> => {
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase() as CamelCaseToSnakeCase<S>;
};

export type DeepCamelCaseProperties<T> = T extends Record<string, unknown>
  ? {
      [K in keyof T as SnakeCaseToCamelCase<
        K & string
      >]: DeepCamelCaseProperties<T[K]>;
    }
  : T extends Array<infer E>
    ? Array<DeepCamelCaseProperties<E>>
    : T;

export const deepCamelCaseProperties = <T>(
  value: T
): DeepCamelCaseProperties<T> => {
  if (Array.isArray(value)) {
    return value.map(deepCamelCaseProperties) as DeepCamelCaseProperties<T>;
  } else if (
    typeof value === 'object' &&
    value !== null &&
    !(value instanceof Date)
  ) {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        snakeCaseToCamelCase(key),
        deepCamelCaseProperties(val),
      ])
    ) as DeepCamelCaseProperties<T>;
  } else {
    return value as DeepCamelCaseProperties<T>;
  }
};

export type DeepSnakeCaseProperties<T> = T extends Record<string, unknown>
  ? {
      [K in keyof T as CamelCaseToSnakeCase<
        K & string
      >]: DeepSnakeCaseProperties<T[K]>;
    }
  : T extends Array<infer E>
    ? Array<DeepSnakeCaseProperties<E>>
    : T;

export const deepSnakeCaseProperties = <T>(
  value: T
): DeepSnakeCaseProperties<T> => {
  if (Array.isArray(value)) {
    return value.map(deepSnakeCaseProperties) as DeepSnakeCaseProperties<T>;
  } else if (
    typeof value === 'object' &&
    value !== null &&
    !(value instanceof Date)
  ) {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        camelCaseToSnakeCase(key),
        deepSnakeCaseProperties(val),
      ])
    ) as DeepSnakeCaseProperties<T>;
  } else {
    return value as DeepSnakeCaseProperties<T>;
  }
};
